// Eén taakregel op /vandaag: afvinkcirkel, taaknaam dominant, project als korte
// tag, duur die je inline aanpast. Drie varianten: sorteerbaar (dagkolom),
// sleepbaar (Morgen / Achterstallig / bronnen) en de losse inhoud (drag-overlay).
import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, InputBase } from '@mui/material';
import {
  DragIndicator as DragIcon,
  RadioButtonUnchecked as UncheckedIcon,
  CheckCircle as CheckedIcon,
  CheckCircleOutline as CheckHoverIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Send as SendIcon,
  CheckBox as TodoIcon,
  StickyNote2 as NoteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { fmtDuur, parseDuur, projectTag, korteTitel, fmtUur } from './dagplanning';

// Rustig palet: één accent (primair) + neutralen. Rood alléén voor te-veel-minuten
// en verlopen datums. Vlakken blijven zo licht dat ze nauwelijks meetellen.
export const KLEUR = {
  tekst: '#1f2933',
  zacht: '#7b848e',
  stil: '#c3c9d0',
  lijn: 'rgba(31,41,51,0.06)',
  vlak: 'transparent',
  achter: '#f2f4f6',
  primair: '#526075',
  primairVlak: '#e8eef7',
  klaar: '#526075',
  telaat: '#dc2626',
};

// Drie tekstmaten op de hele pagina (plus de paginatitel).
export const MAAT = { tekst: '0.9rem', klein: '0.74rem' };

const DAGEN = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
// Jaartal alleen als de datum niet in het huidige jaar valt ("do 31 jul 2025"): een
// achterstallige deadline van vorig jaar las anders als deze zomer (wiki 27 sep).
export function korteDatum(d, nu = new Date()) {
  if (!d) return '';
  const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
  const jaar = dt.getFullYear() !== nu.getFullYear() ? ` ${dt.getFullYear()}` : '';
  return `${DAGEN[dt.getDay()]} ${dt.getDate()} ${MAANDEN[dt.getMonth()]}${jaar}`;
}

/** Project als gewone, gedempte tekst ("9688 · Manifestfilm"); volledige naam in de tooltip. */
export function ProjectTag({ naam, maxWidth = 220 }) {
  const tag = projectTag(naam);
  if (!tag) return null;
  return (
    <Tooltip title={naam} enterDelay={500}>
      <Box component="span" sx={{
        display: 'inline-block', maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        fontSize: MAAT.klein, fontWeight: 400, color: KLEUR.zacht, verticalAlign: 'middle',
      }}>
        {tag.label}
      </Box>
    </Tooltip>
  );
}

/** Afvinkcirkel: grijs, groen vinkje bij hover, gevuld als afgerond. */
export function Afvinker({ klaar, onClick, grootte = 20, titel }) {
  return (
    <Tooltip title={titel || (klaar ? 'Weer openzetten' : 'Afronden')} enterDelay={400}>
      <IconButton
        size="small"
        aria-label={klaar ? 'Weer openzetten' : 'Afronden'}
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        onPointerDown={(e) => e.stopPropagation()}
        sx={{
          p: 0.25, flexShrink: 0, color: klaar ? KLEUR.klaar : KLEUR.stil,
          '& .hover': { display: 'none' },
          '&:hover .rust': { display: klaar ? 'block' : 'none' },
          '&:hover .hover': { display: klaar ? 'none' : 'block', color: KLEUR.primair },
        }}
      >
        {klaar
          ? <CheckedIcon className="rust" sx={{ fontSize: grootte }} />
          : <>
            <UncheckedIcon className="rust" sx={{ fontSize: grootte }} />
            <CheckHoverIcon className="hover" sx={{ fontSize: grootte }} />
          </>}
      </IconButton>
    </Tooltip>
  );
}

/** Duur-pil: klik om te wijzigen ("1u30", "45m", "90"). */
function DuurPil({ minuten, onWijzig, uit }) {
  const [bewerk, setBewerk] = useState(false);
  const [waarde, setWaarde] = useState('');
  if (!minuten) return null;
  const { min, bron, dagen } = minuten;
  const uitleg = bron === 'standaard' ? 'Geschat (geen uren in de taak) — klik om aan te passen'
    : bron === 'dagdeel' ? `Uren verdeeld over ${dagen} werkdagen — klik om voor vandaag aan te passen`
      : bron === 'eigen' ? 'Zelf ingesteld voor vandaag — klik om aan te passen'
        : 'Uren uit de taak — klik om aan te passen';
  const opslaan = () => {
    const m = parseDuur(waarde);
    setBewerk(false);
    if (m != null && m > 0 && m !== min) onWijzig?.(m);
  };
  if (bewerk) {
    return (
      <InputBase
        autoFocus
        value={waarde}
        onChange={(e) => setWaarde(e.target.value)}
        onBlur={opslaan}
        onKeyDown={(e) => { if (e.key === 'Enter') opslaan(); if (e.key === 'Escape') setBewerk(false); }}
        onPointerDown={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': 'Duur', style: { padding: 0, textAlign: 'center' } }}
        sx={{ width: 52, fontSize: MAAT.klein, border: `1px solid ${KLEUR.primair}`, borderRadius: '6px', px: 0.5, height: 22 }}
      />
    );
  }
  return (
    <Tooltip title={uitleg} enterDelay={400}>
      <Box
        component="button"
        type="button"
        disabled={!onWijzig}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); setWaarde(fmtDuur(min)); setBewerk(true); }}
        sx={{
          all: 'unset', cursor: onWijzig ? 'text' : 'default', flexShrink: 0,
          fontSize: MAAT.klein, fontVariantNumeric: 'tabular-nums',
          color: KLEUR.zacht, borderRadius: '4px', px: 0.5, lineHeight: '20px', minWidth: 30, textAlign: 'right',
          '&:hover': onWijzig ? { color: KLEUR.primair, textDecoration: 'underline' } : {},
        }}
      >
        {bron === 'standaard' ? '~' : ''}{fmtDuur(min)}
      </Box>
    </Tooltip>
  );
}

/** Deadline-label: rood alleen als hij verlopen is. */
function DeadlineLabel({ datum, vandaag }) {
  if (!datum) return null;
  const d = String(datum).slice(0, 10);
  const telaat = vandaag && d < vandaag;
  return (
    <Typography component="span" sx={{ fontSize: MAAT.klein, color: telaat ? KLEUR.telaat : KLEUR.zacht }}>
      {telaat ? 'deadline ' : 'tot '}{korteDatum(d)}
    </Typography>
  );
}


/** De zichtbare inhoud van een regel — gedeeld door alle varianten. */
export function RijInhoud({
  item, minuten, begintijd, blok, werkdagEind, vandaag, handle, isOverlay, compact, morgenLabel = 'morgen',
  onToggle, onMoveDay, moveDayDirection, onDatum, onKlik, onDuur, onMarkSent, onVandaag,
}) {
  const titel = korteTitel(item.title, item.projectName);
  const eind = item._raw?.fields?.EndDateUitvoerder || item.deadline || null;
  const toonEind = eind && item.startDate && String(eind).slice(0, 10) !== item.startDate;
  const prio1 = item.priority === 'Prio 1' && !item.isComplete;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 0.75,
      px: 1, py: compact ? 0.75 : 1, borderRadius: '8px',
      backgroundColor: isOverlay ? '#fff' : KLEUR.vlak,
      transition: 'background-color .15s',
      '&:hover': { backgroundColor: '#f7f8fa' },
      '&:hover .rij-acties, &:hover .rij-handle, &:focus-within .rij-acties': { opacity: 1 },
      ...(isOverlay && { boxShadow: '0 8px 24px rgba(31,41,51,0.14)', cursor: 'grabbing' }),
    }}>
      <Box className="rij-handle" {...(handle || {})} sx={{
        cursor: 'grab', display: 'flex', alignItems: 'center', alignSelf: 'center',
        opacity: isOverlay ? 1 : 0, transition: 'opacity .15s', ml: -0.5, touchAction: 'none',
      }}>
        <DragIcon sx={{ fontSize: 16, color: KLEUR.stil }} />
      </Box>
      {onToggle
        ? <Afvinker klaar={item.isComplete} onClick={() => onToggle(item)} />
        : <Box sx={{ width: 4 }} />}
      <Box sx={{ flex: 1, minWidth: 0, pt: '2px' }}>
        <Typography
          onClick={(e) => onKlik?.(item, e)}
          title={titel !== item.title ? item.title : undefined}
          sx={{
            fontSize: MAAT.tekst, fontWeight: 500, lineHeight: 1.35,
            color: item.isComplete ? KLEUR.stil : KLEUR.tekst,
            textDecoration: item.isComplete ? 'line-through' : 'none',
            cursor: onKlik ? 'pointer' : 'default',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            '&:hover': onKlik ? { color: KLEUR.primair } : {},
          }}
        >
          {prio1 && (
            <Tooltip title="Prio 1"><Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: KLEUR.primair, mr: 0.75, verticalAlign: 'middle', mb: '2px' }} /></Tooltip>
          )}
          {titel}
        </Typography>
        {(begintijd != null || item.projectName || (toonEind && !item.isComplete) || item.source !== 'airtable' || item.priority === 'Opgestuurd') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
            {begintijd != null && (
              <Typography component="span" sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, fontVariantNumeric: 'tabular-nums' }}>
                {fmtUur(begintijd)}
              </Typography>
            )}
            {item.projectName && <ProjectTag naam={item.projectName} maxWidth={compact ? 170 : 320} />}
            {toonEind && !item.isComplete && <DeadlineLabel datum={eind} vandaag={vandaag} />}
            {item.source === 'mstodo' && <Tooltip title="Microsoft To Do"><TodoIcon sx={{ fontSize: 13, color: KLEUR.stil }} /></Tooltip>}
            {item.source === 'action' && <Tooltip title="Actiepunt uit notulen"><NoteIcon sx={{ fontSize: 13, color: KLEUR.stil }} /></Tooltip>}
            {item.priority === 'Opgestuurd' && (
              <Typography component="span" sx={{ fontSize: MAAT.klein, color: KLEUR.zacht }}>opgestuurd</Typography>
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, pt: '1px' }}>
        <Box className="rij-acties" sx={{ display: 'flex', gap: 0.25, opacity: 0, transition: 'opacity .15s' }}>
          {onVandaag && !item.isComplete && (
            <Tooltip title="Naar vandaag">
              <IconButton size="small" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onVandaag(item); }} sx={{ p: 0.25, color: KLEUR.zacht }}>
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {onMoveDay && !item.isComplete && (
            <Tooltip title={moveDayDirection === 'tomorrow' ? `Naar ${morgenLabel}` : 'Naar vandaag'}>
              <IconButton size="small" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onMoveDay(item); }} sx={{ p: 0.25, color: KLEUR.zacht }}>
                {moveDayDirection === 'tomorrow' ? <ArrowForwardIcon sx={{ fontSize: 16 }} /> : <ArrowBackIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          )}
          {onDatum && !item.isComplete && (
            <Tooltip title="Andere dag">
              <IconButton size="small" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onDatum(item, e.currentTarget); }} sx={{ p: 0.25, color: KLEUR.zacht }}>
                <CalendarIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {item.isComplete && onMarkSent && item.priority !== 'Opgestuurd' && item._airtableId && (
            <Tooltip title="Markeer als Opgestuurd">
              <IconButton size="small" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onMarkSent(item); }} sx={{ p: 0.25, color: KLEUR.zacht }}>
                <SendIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {blok && !item.isComplete && (
          <Tooltip enterDelay={400} title={blok.over
            ? `Gepland ${fmtUur(blok.s)}–${fmtUur(blok.e)}: loopt na het einde van je werkdag (${fmtUur(werkdagEind)})`
            : blok.vast ? `Tijdblok ${fmtUur(blok.s)}–${fmtUur(blok.e)}` : `Gepland ${fmtUur(blok.s)}–${fmtUur(blok.e)} (volgorde van je lijst)`}>
            <Typography component="span" sx={{
              fontSize: MAAT.klein, fontVariantNumeric: 'tabular-nums',
              color: blok.over ? KLEUR.telaat : blok.vast ? KLEUR.tekst : KLEUR.zacht,
            }}>
              {fmtUur(blok.s)} ·
            </Typography>
          </Tooltip>
        )}
        {minuten && <DuurPil minuten={minuten} uit={item.isComplete} onWijzig={onDuur && !item.isComplete ? (m) => onDuur(item, m) : null} />}
      </Box>
    </Box>
  );
}

/** Regel in de dagkolom: sorteerbaar (volgorde) én naar tijdlijn/morgen sleepbaar. */
export function SorteerbareRij({ item, ...rest }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id, data: { item, bron: 'vandaag' },
  });
  return (
    <Box ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 }} {...attributes} {...listeners} sx={{ touchAction: 'none' }}>
      <RijInhoud item={item} {...rest} />
    </Box>
  );
}

/** Regel elders (Morgen, Achterstallig, bronnen): hele regel is de sleepgreep. */
export function SleepbareRij({ item, bron, ...rest }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${bron}:${item.id}`, data: { item, bron } });
  return (
    <Box ref={setNodeRef} {...attributes} {...listeners} sx={{ opacity: isDragging ? 0.35 : 1, cursor: 'grab', touchAction: 'none' }}>
      <RijInhoud item={item} {...rest} />
    </Box>
  );
}
